/**
 * This will allow as many calls as you like but will only
 * trigger a new promise once the last is complete and has resolved.
 *
 * It is useful for effectively debouncing API calls but does require
 * some care (see caveats).
 *
 * Example:
 *
 * const apiCall = () => axios.get('/some/endpoint').then(prop('data'));
 * const callDebounced = throttlePromise(apiCall);
 * export default callDebounced;
 *
 * Using the above example, no matter how many times the client code
 * calls your API method, only one call at a time will be sent.
 *
 * **caveats / gotchas**
 *
 * This functionality is intended for making repeated, _similar_ calls.
 * If the params being input change from call to call, this is likely _not_
 * suitable for your usecase as the call will resolve with the first called
 * params only. (@proposal memoise based on calling params and remove this
 * warning!)
 *
 * @param {Function} doRequest must return a promise
 */
var throttle = function throttle(doRequest) {
  var request;

  // catches and transparently passes a result, marking promise
  // as complete en route.
  var done = function done(resp) {
    request.isInFlight = false;
    return resp;
  };

  // catches and rethrows an error, marking the promise completed
  // as it does so.
  var failure = function failure(err) {
    request.isInFlight = false;
    throw err;
  };
  return function () {
    // if nothing is running, start something running...
    if (!request || !request.isInFlight) {
      request = doRequest.apply(void 0, arguments).then(done).catch(failure);

      // ...and mark it as running
      request.isInFlight = true;
    }
    return request;
  };
};

/**
 * Convenience method for handling promises. Allows for (e.g.) copying data from
 * a response and passing the original response on to the next promise in the
 * chain without too much clutter.
 *
 * @param {Function} fn The function to call mid-chain
 * @return Function
 */
var passThrough = function passThrough(fn) {
  return function (data) {
    fn(data);
    return data;
  };
};
export { throttle, passThrough };